import { createFileRoute } from '@tanstack/react-router'
import { Eye, EyeOff, MessageSquareText, Sparkles, Star } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ModerationReview } from '@/types/ecommerce'
import { useModerationReviews, usePublishReviewReply, useSetReviewHidden } from '@/lib/reviews'

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
      ))}
    </div>
  )
}

/** Store reply: published text shown read-only; otherwise an editable box seeded from the
 * AI draft (if the async ReviewLowRatingConsumer has generated one yet) that a manager can
 * edit before publishing — the draft is a suggestion only, never shown to customers. */
function ReplyBox({ review }: { review: ModerationReview }) {
  const publish = usePublishReviewReply()
  const [text, setText] = useState(review.storeReply ?? review.storeReplyDraft ?? '')

  // Background AI drafting (ReviewLowRatingConsumer) can land after the page is already open —
  // pick up a newly-arrived draft as long as the manager hasn't started typing their own reply.
  useEffect(() => {
    if (!text && review.storeReplyDraft) setText(review.storeReplyDraft)
  }, [review.storeReplyDraft]) // eslint-disable-line react-hooks/exhaustive-deps

  if (review.storeReply) {
    return (
      <div className="text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 rounded-lg p-2 mt-1">
        <span className="font-medium text-gray-500">Store reply: </span>
        {review.storeReply}
      </div>
    )
  }

  return (
    <div className="mt-1 space-y-1">
      {review.storeReplyDraft && !text && (
        <div className="flex items-center gap-1 text-[11px] text-purple-600 dark:text-purple-400">
          <Sparkles className="h-3 w-3" /> AI draft ready
        </div>
      )}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={review.rating < 3 ? 'AI draft will appear here shortly…' : 'Write a store reply (optional)'}
        rows={2}
        className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2"
      />
      <button
        onClick={() => publish.mutate({ id: review.id, reply: text })}
        disabled={!text.trim() || publish.isPending}
        className="text-xs px-2 py-1 rounded-lg bg-purple-600 text-white disabled:opacity-40"
      >
        {publish.isPending ? 'Publishing…' : 'Publish reply'}
      </button>
    </div>
  )
}

function ReviewsPage() {
  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useModerationReviews()
  const setHidden = useSetReviewHidden()

  const reviews = data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <MessageSquareText className="w-6 h-6" /> Review Moderation
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Every product review, real and customer-submitted. Hidden reviews are excluded from the storefront and from each product's rating average.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading reviews…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load reviews: {(error as Error)?.message}.</p>
        ) : reviews.length === 0 ? (
          <p className="p-6 text-gray-500">No reviews yet.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Product</th>
                <th className="px-4 py-2">Rating</th>
                <th className="px-4 py-2">Review</th>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Posted</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-700 ${r.isHidden ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{r.productName}</td>
                  <td className="px-4 py-3">
                    <Stars rating={r.rating} />
                  </td>
                  <td className="px-4 py-3 max-w-sm">
                    {r.title && <div className="font-medium text-gray-800 dark:text-gray-100">{r.title}</div>}
                    <div className="text-gray-600 dark:text-gray-300 line-clamp-2">{r.body}</div>
                    <ReplyBox review={r} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.customerId}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{new Date(r.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setHidden.mutateAsync({ id: r.id, hidden: !r.isHidden })}
                        disabled={setHidden.isPending}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-40"
                        title={r.isHidden ? 'Unhide review' : 'Hide review'}
                      >
                        {r.isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {hasNextPage && (
        <div className="flex justify-center">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}

export const Route = createFileRoute('/(admin)/reviews/')({
  component: ReviewsPage,
})
